import { useState, useCallback, useRef } from "react";
import { getAllDatasets, getDataset } from "../services/datasetsService";

export const useDatasets = () => {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Cache to prevent duplicate requests
  const cacheRef = useRef(new Map());
  const pendingRequestsRef = useRef(new Map());

  const getAvailableDatasets = useCallback(async () => {
    setIsLoading(true);
    setError(false);

    try {
      const response = await getAllDatasets();
      setIsLoading(false);
      localStorage.setItem("dataset", response);
      return response;
    } catch (error) {
      setIsLoading(false);
      setError(true);
      return "Something went wrong";
    }
  }, []);

  const getSpecificDataset = useCallback(async (id) => {
    if (!id) {
      return null;
    }

    // Check cache first
    if (cacheRef.current.has(id)) {
      return cacheRef.current.get(id);
    }

    // Check if there's already a pending request for this ID
    if (pendingRequestsRef.current.has(id)) {
      return pendingRequestsRef.current.get(id);
    }

    setIsLoading(true);
    setError(false);

    // Create the promise and cache it immediately to prevent duplicate requests
    const promise = (async () => {
      try {
        const response = await getDataset(id);

        // Cache the successful response
        cacheRef.current.set(id, response);

        setIsLoading(false);
        return response;
      } catch (error) {
        setIsLoading(false);
        setError(true);
        console.error(`Error fetching dataset ${id}:`, error);
        throw error;
      } finally {
        // Remove from pending requests
        pendingRequestsRef.current.delete(id);
      }
    })();

    // Store the pending promise
    pendingRequestsRef.current.set(id, promise);

    return promise;
  }, []);

  // Clear cache method (optional, for manual cache clearing)
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
    pendingRequestsRef.current.clear();
  }, []);

  return {
    getAvailableDatasets,
    getSpecificDataset,
    error,
    isLoading,
    clearCache
  };
};
